import { useState } from 'react';
import { MessageSquarePlus, HelpCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const FeedbackButton = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // 'feedback' | 'support'
  const [loading, setLoading] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ content: '', category: 'feature_request' });
  const [supportForm, setSupportForm] = useState({ subject: '', description: '', priority: 'normal' });

  const handleSubmitFeedback = async () => {
    if (!feedbackForm.content.trim()) { toast.error('Please enter your feedback'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/feedback`, feedbackForm, { withCredentials: true });
      toast.success('Feedback submitted! Thank you.');
      setFeedbackForm({ content: '', category: 'feature_request' });
      setMode(null);
      setOpen(false);
    } catch { toast.error('Failed to submit feedback'); }
    setLoading(false);
  };

  const handleSubmitSupport = async () => {
    if (!supportForm.description.trim()) { toast.error('Please describe your issue'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/support-tickets`, supportForm, { withCredentials: true });
      toast.success('Support ticket submitted! We\'ll get back to you.');
      setSupportForm({ subject: '', description: '', priority: 'normal' });
      setMode(null);
      setOpen(false);
    } catch { toast.error('Failed to submit ticket'); }
    setLoading(false);
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        data-testid="feedback-floating-btn"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
      >
        {open ? <X size={20} /> : <MessageSquarePlus size={20} />}
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-40 w-80 sm:w-96 bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200" data-testid="feedback-panel">
          {!mode ? (
            <>
              <div className="p-5 border-b border-slate-100">
                <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>How can we help?</h3>
                <p className="text-sm text-slate-500 mt-0.5">Choose an option below</p>
              </div>
              <div className="p-4 space-y-3">
                <button
                  data-testid="feedback-option-btn"
                  onClick={() => setMode('feedback')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-200 transition-colors">
                    <MessageSquarePlus size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Feature Request / Feedback</p>
                    <p className="text-xs text-slate-500">Suggest improvements or new features</p>
                  </div>
                </button>
                <button
                  data-testid="support-option-btn"
                  onClick={() => setMode('support')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all text-left group"
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 transition-colors">
                    <HelpCircle size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">Support / Help</p>
                    <p className="text-xs text-slate-500">Report a bug or get assistance</p>
                  </div>
                </button>
              </div>
            </>
          ) : mode === 'feedback' ? (
            <>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Send Feedback</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Your input shapes the product</p>
                </div>
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600 p-1"><X size={16} /></button>
              </div>
              <div className="p-4 space-y-3">
                <select
                  data-testid="feedback-category-select"
                  value={feedbackForm.category}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                >
                  <option value="feature_request">Feature Request</option>
                  <option value="improvement">Improvement</option>
                  <option value="general">General Feedback</option>
                </select>
                <textarea
                  data-testid="feedback-content-input"
                  value={feedbackForm.content}
                  onChange={(e) => setFeedbackForm({ ...feedbackForm, content: e.target.value })}
                  placeholder="Tell us what you'd like to see..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 resize-none"
                  autoFocus
                />
                <Button
                  data-testid="feedback-submit-btn"
                  onClick={handleSubmitFeedback}
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                  <Send size={15} className="mr-2" />
                  {loading ? 'Sending...' : 'Submit Feedback'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Get Support</h3>
                  <p className="text-xs text-slate-500 mt-0.5">We'll look into it right away</p>
                </div>
                <button onClick={() => setMode(null)} className="text-slate-400 hover:text-slate-600 p-1"><X size={16} /></button>
              </div>
              <div className="p-4 space-y-3">
                <input
                  data-testid="support-subject-input"
                  type="text"
                  value={supportForm.subject}
                  onChange={(e) => setSupportForm({ ...supportForm, subject: e.target.value })}
                  placeholder="Subject (optional)"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                  autoFocus
                />
                <select
                  data-testid="support-priority-select"
                  value={supportForm.priority}
                  onChange={(e) => setSupportForm({ ...supportForm, priority: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                >
                  <option value="low">Low Priority</option>
                  <option value="normal">Normal</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
                <textarea
                  data-testid="support-description-input"
                  value={supportForm.description}
                  onChange={(e) => setSupportForm({ ...supportForm, description: e.target.value })}
                  placeholder="Describe your issue or question..."
                  rows={4}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500 resize-none"
                />
                <Button
                  data-testid="support-submit-btn"
                  onClick={handleSubmitSupport}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
                >
                  <Send size={15} className="mr-2" />
                  {loading ? 'Sending...' : 'Submit Ticket'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
